import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import notificationService from '../services/notificationService';
import { ToastContainer } from '../components/ToastNotification';

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
  const [permissionStatus, setPermissionStatus] = useState('default');
  const [isInitialized, setIsInitialized] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [fcmToken, setFcmToken] = useState(null);

  // Initialize notification service
  useEffect(() => {
    const init = async () => {
      if (!notificationService.isSupported()) {
        console.warn('Push notifications not supported');
        setIsInitialized(true);
        return;
      }

      await notificationService.initialize();
      setPermissionStatus(Notification.permission);
      setIsInitialized(true);
    };

    init();
  }, []);

  // Listen for service worker messages to play sound (for background tab notifications)
  useEffect(() => {
    const handleServiceWorkerMessage = (event) => {
      if (event.data?.type === 'PLAY_NOTIFICATION_SOUND') {
        console.log('🔊 Service worker requested sound playback');
        const soundUrl = event.data.soundUrl || CUSTOM_SOUND_URL;
        try {
          const audio = new Audio(soundUrl);
          audio.volume = 1.0;
          audio.play()
            .then(() => console.log('🔊 Background notification sound played'))
            .catch((err) => console.warn('🔊 Failed to play background sound:', err.message));
        } catch (error) {
          console.warn('Failed to play notification sound from SW message:', error);
        }
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, []);

  // Add toast notification
  const addToast = useCallback((notification) => {
    const id = Date.now() + Math.random().toString(36).substr(2, 9);
    const toast = { ...notification, id };

    setToasts((prev) => [...prev, toast]);

    // Play sound for new orders
    if (notification.type === 'new_order') {
      playNotificationSound();
    }

    return id;
  }, []);

  // Remove toast notification
  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Custom notification sound URL
  const CUSTOM_SOUND_URL = 'https://pub-7ccc1a05d7f24ac88e57c3427b104297.r2.dev/sounds/restaurant/sounds-restaurant-1781034058176-2c7b89fc9d68f6a2.mp3';

  // Play notification sound using custom R2 audio file
  const playNotificationSound = () => {
    try {
      const audio = new Audio(CUSTOM_SOUND_URL);
      audio.volume = 1.0;
      audio.play()
        .then(() => console.log('🔊 NotificationContext: Custom sound played successfully'))
        .catch((err) => console.warn('🔊 NotificationContext: Failed to play sound:', err.message));
    } catch (error) {
      console.warn('Failed to play notification sound:', error);
    }
  };

  // Request permission and get token
  const requestPermission = async () => {
    try {
      const granted = await notificationService.requestPermission();
      setPermissionStatus(granted ? 'granted' : 'denied');

      if (granted) {
        const token = await notificationService.getToken();
        setFcmToken(token);
        return token;
      }

      return null;
    } catch (error) {
      console.error('Failed to request permission:', error);
      return null;
    }
  };

  // Register token with server (call after login)
  const registerWithServer = async () => {
    try {
      if (permissionStatus !== 'granted') {
        const token = await requestPermission();
        if (token) {
          await notificationService.registerTokenWithServer(token);
          return true;
        }
        return false;
      }

      // Already have permission, just get token and register
      const token = await notificationService.getToken(true);
      setFcmToken(token);
      return !!token;
    } catch (error) {
      console.error('Failed to register with server:', error);
      return false;
    }
  };

  // Unregister token from server (call on logout)
  const unregisterFromServer = async () => {
    try {
      await notificationService.unregisterTokenFromServer();
      setFcmToken(null);
      return true;
    } catch (error) {
      console.error('Failed to unregister from server:', error);
      return false;
    }
  };

  // Subscribe to foreground messages
  const subscribeToMessages = useCallback((onNotification) => {
    notificationService.onMessage((notification) => {
      // Add toast for foreground notifications
      addToast(notification);

      // Call custom handler if provided
      if (onNotification) {
        onNotification(notification);
      }
    });
  }, [addToast]);

  // Handle toast click (navigation)
  const handleToastClick = useCallback((notification) => {
    // Navigation is handled by the component that renders the ToastContainer
    console.log('Toast clicked:', notification);
  }, []);

  const value = {
    permissionStatus,
    isInitialized,
    isSupported: notificationService.isSupported(),
    fcmToken,
    toasts,
    addToast,
    removeToast,
    requestPermission,
    registerWithServer,
    unregisterFromServer,
    subscribeToMessages
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <ToastContainer
        toasts={toasts}
        onRemove={removeToast}
        onClick={handleToastClick}
        position="top-right"
      />
    </NotificationContext.Provider>
  );
};

// Hook for using notification context
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

// Wrapper component that handles navigation
export const NotificationNavigator = ({ children }) => {
  const navigate = useNavigate();
  const { subscribeToMessages } = useNotifications();

  useEffect(() => {
    // Subscribe to foreground messages
    subscribeToMessages((notification) => {
      // Handle notification click navigation
      if (notification.orderId) {
        // Could auto-navigate or just show toast
        console.log('New order notification:', notification.orderId);
      }
    });

    // Listen for service worker messages (notification clicks)
    const handleMessage = (event) => {
      if (event.data?.type === 'NOTIFICATION_CLICK') {
        const { orderId } = event.data.data || {};
        if (orderId) {
          navigate('/restaurant/orders');
        }
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleMessage);
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      }
    };
  }, [navigate, subscribeToMessages]);

  return children;
};

export default NotificationContext;
